'use client';

import { useEffect, useRef, useState } from 'react';

export type ImageTransformVariant = 'flip-image' | 'rotate-image';

export default function BrowserImageTransform({ variant }: { variant: ImageTransformVariant }) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [sourceName, setSourceName] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSourceName(file.name.replace(/\.[^.]+$/, ''));
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rotated = rotation % 180 !== 0;
    const w = image.naturalWidth;
    const h = image.naturalHeight;
    canvas.width = rotated ? h : w;
    canvas.height = rotated ? w : h;
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.drawImage(image, -w / 2, -h / 2);
    ctx.restore();
    canvas.toBlob((blob) => {
      if (blob) setDownloadUrl(URL.createObjectURL(blob));
    }, 'image/png');
  }, [image, flipH, flipV, rotation]);

  const title = variant === 'flip-image' ? 'Flip an image' : 'Rotate an image';
  const badge = variant === 'flip-image' ? 'Flip' : 'Rotate';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{badge}</span>
        <h2 className="tool-live-title">{title}</h2>
        <p className="tool-live-subtitle">
          Upload an image and it is processed entirely in your browser. Nothing is uploaded to a
          server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>
          {image ? (
            <div className="tool-live-actions">
              {variant === 'flip-image' ? (
                <>
                  <button
                    className="button button-secondary"
                    onClick={() => setFlipH((v) => !v)}
                    type="button"
                  >
                    Flip horizontal
                  </button>
                  <button
                    className="button button-secondary"
                    onClick={() => setFlipV((v) => !v)}
                    type="button"
                  >
                    Flip vertical
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="button button-secondary"
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    type="button"
                  >
                    Rotate 90° right
                  </button>
                  <button
                    className="button button-secondary"
                    onClick={() => setRotation((r) => (r + 270) % 360)}
                    type="button"
                  >
                    Rotate 90° left
                  </button>
                </>
              )}
              {downloadUrl ? (
                <a
                  className="button button-secondary"
                  download={`${sourceName ?? variant}-${variant}.png`}
                  href={downloadUrl}
                >
                  Download PNG
                </a>
              ) : null}
            </div>
          ) : (
            <p className="tool-live-panel-note">Pick an image to get started.</p>
          )}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
